import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DatePicker } from '@shopify/polaris';
import {
  AlertTriangleIcon, CalendarIcon, CheckCircleIcon, ChevronDownIcon, PrintIcon, RefreshIcon, SearchIcon, XSmallIcon,
} from '@shopify/polaris-icons';
import { Ic, StatusPill } from './ui';
import { useAuth } from './AuthComponent';
import { canOpenSettings } from './Settings/Settings';

const API_BASE_URL = window.location.origin;

const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('NO_TOKEN');
  const isFormData = options._isFormData;
  const headers = { 'Authorization': `Bearer ${token}`, ...options.headers };
  if (!isFormData) headers['Content-Type'] = 'application/json';
  const { _isFormData, ...rest } = options;
  const response = await fetch(url, { ...rest, headers });
  if (response.status === 401) throw new Error('AUTH_FAILED');
  return response;
};

const LOCATION_OPTIONS = [
  { label: 'Teams Lancaster', value: 'lancaster' },
  { label: 'Tinton Falls Warehouse', value: 'tinton-falls' },
  { label: 'Teams Brookfield', value: 'brookfield' },
  { label: 'Teams Carrollton', value: 'carrollton' },
];

const STATUS_COLORS = {
  'received':           { bg: '#e3f1df', color: '#186a17' },
  'fully_received':     { bg: '#e3f1df', color: '#186a17' },
  'partially_received': { bg: '#fdf4e7', color: '#7d5500' },
  'ordered':            { bg: '#e8f0fe', color: '#1a56db' },
  'cancelled':          { bg: '#fde8e8', color: '#c0392b' },
  'closed':             { bg: '#fde8e8', color: '#c0392b' },
};


function formatDate(d) {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Print card — plain HTML, no Polaris ───────────────────────────────────────
function POPrintCard({ po, location }) {
  const locationLabel = LOCATION_OPTIONS.find(o => o.value === location)?.label || location;

  const items = (po.lineItems?.edges || []).slice().sort((a, b) => {
    const la = (a.node?.bin_location || '').toLowerCase();
    const lb = (b.node?.bin_location || '').toLowerCase();
    return la < lb ? -1 : la > lb ? 1 : 0;
  });

  const statusKey = (po.receiptStatus || po.status || '').toLowerCase().replace(/ /g, '_');
  const statusStyle = STATUS_COLORS[statusKey] || { bg: '#e8f0fe', color: '#1a56db' };
  const statusLabel = (po.receiptStatus || po.status || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  const supplierName = (po.supplier || po.supplierTarget || {}).name || '—';
  const destName = (po.destination || po.destinationTarget || {}).name || '—';
  const orderedDate = (po.orderedAt || '').slice(0, 10) || '—';

  const now = new Date();
  const footerTs = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const totalQty = items.reduce((sum, e) => sum + (e.node?.totalQuantity || e.node?.acceptedQuantity || 0), 0);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', paddingBottom: 32 }}>

      {/* ── Header ── */}
      <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
              {po.name || 'Purchase Order'}
            </div>
            <div style={{ fontSize: 12, color: '#6d7175', marginTop: 3 }}>Soccer Post</div>
          </div>
          <span style={{
            backgroundColor: statusStyle.bg, color: statusStyle.color,
            borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 600,
            display: 'inline-block', marginTop: 4, whiteSpace: 'nowrap',
          }}>
            {statusLabel}
          </span>
        </div>
        <hr style={{ border: 'none', borderTop: '2px solid #303030', margin: '0 0 16px' }} />
      </div>

      {/* ── Details card ── */}
      <div style={{
        border: '1px solid #e1e3e5', borderRadius: 8, padding: '10px 14px',
        marginBottom: 12, backgroundColor: '#fafafa',
        pageBreakInside: 'avoid', breakInside: 'avoid',
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Details</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 28px', fontSize: 13 }}>
          <span><strong>Ordered:</strong> {orderedDate}</span>
          <span><strong>Supplier:</strong> {supplierName}</span>
          <span><strong>Destination:</strong> {destName}</span>
          <span><strong>Total Qty:</strong> {totalQty}</span>
          <span><strong>Bin Location:</strong> {locationLabel}</span>
          {po.totalCost?.amount && (
            <span><strong>Total Cost:</strong> ${po.totalCost.amount} {po.totalCost.currencyCode}</span>
          )}
        </div>
      </div>

      {/* ── Products Table ── */}
      {items.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ backgroundColor: '#f6f6f7' }}>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', width: 44 }}></th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600 }}>Product</th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: '16%' }}>SKU</th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: '18%' }}>Bin</th>
              <th style={{ padding: '7px 8px', textAlign: 'center', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: 44 }}>Qty</th>
              <th style={{ padding: '7px 8px', textAlign: 'center', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: 52 }}>Picked</th>
            </tr>
          </thead>
          <tbody>
            {items.map((edge, i) => {
              const item = edge.node || edge;
              const sku = item.inventoryItem?.variant?.sku || item.supplierSku || '—';
              const imageUrl = item.image_url || item.image?.transformedSrc;
              const binRaw = item.bin_location || '';
              const binDisplay = binRaw && binRaw.includes(' - ') ? binRaw.split(' - ').slice(1).join(' - ') : (binRaw || '—');
              const qty = item.totalQuantity || item.acceptedQuantity || 0;
              const variantTitle = item.description && item.description !== 'Default Title' ? item.description : '';
              return (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '5px 8px', verticalAlign: 'middle' }}>
                    {imageUrl
                      ? <img src={imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #e1e3e5', display: 'block' }} />
                      : <div style={{ width: 36, height: 36, borderRadius: 4, border: '1px solid #e1e3e5', backgroundColor: '#f6f6f7' }} />
                    }
                  </td>
                  <td style={{ padding: '5px 8px', verticalAlign: 'middle' }}>
                    <div style={{ fontWeight: 500, color: '#202223', lineHeight: 1.3 }}>{item.title || 'Unknown'}</div>
                    {variantTitle && <div style={{ color: '#6d7175', fontSize: 11, marginTop: 1 }}>{variantTitle}</div>}
                  </td>
                  <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontSize: 11, verticalAlign: 'middle' }}>{sku}</td>
                  <td style={{ padding: '5px 8px', fontSize: 11, verticalAlign: 'middle', color: binRaw ? '#005bd3' : '#aaa', fontWeight: binRaw ? 500 : 400 }}>
                    {binDisplay}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 700, verticalAlign: 'middle' }}>{qty}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <div style={{ width: 18, height: 18, border: '1.5px solid #303030', borderRadius: 3, display: 'inline-block' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p style={{ color: '#999', fontSize: 13 }}>No line items found.</p>
      )}

      {/* ── Footer ── */}
      <div style={{ marginTop: 20, paddingTop: 8, borderTop: '1px solid #e1e3e5', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6d7175' }}>
        <span>Generated {footerTs}</span>
        <span>{locationLabel}</span>
      </div>
    </div>
  );
}

// Status tones for uh.css StatusPill: Shopify's receipt statuses.
function statusTone(status) {
  const s = (status || '').toLowerCase();
  if (s === 'received' || s === 'fully_received') return 'ful';
  if (s === 'partially_received') return 'hold';
  if (s === 'cancelled' || s === 'closed') return 'can';
  if (s === 'draft') return 'unf';
  if (s === 'ordered') return 'par';
  return 'neu';
}
const statusText = (status) => (status || 'UNKNOWN').replace(/_/g, ' ').toUpperCase();

const STATUS_FILTERS = [
  ['', 'Status: all'], ['ORDERED', 'Ordered'], ['DRAFT', 'Draft'], ['PARTIALLY_RECEIVED', 'Partially received'],
  ['RECEIVED', 'Received'], ['CLOSED', 'Closed'],
];

function ProductImage({ imageUrl, productTitle }) {
  const [imageLoaded, setImageLoaded] = useState(true);
  useEffect(() => { setImageLoaded(true); }, [imageUrl]);
  if (!imageUrl || !imageLoaded) {
    return <div className="uh-tr-img initials" aria-hidden="true">{(productTitle || 'PR').substring(0, 2).toUpperCase()}</div>;
  }
  return <div className="uh-tr-img"><img src={imageUrl} alt={productTitle} onError={() => setImageLoaded(false)} /></div>;
}

// Age of the session for the one-line card: "less than a day ago", "6 days ago".
function sessionAgeText(status) {
  if (!status || !status.config_age) return '';
  const a = status.config_age;
  return a.toLowerCase() === 'less than 1 day' ? 'less than a day ago' : `${a} ago`;
}

// Purchase Orders: find POs, tick the ones to pick, print their pick sheets.
// Laid out like Transfers (frontend/src/TransferReports.jsx): the uh-tr-* classes
// in theme/uh.css are shared by both list-and-print pages. The Shopify session
// (HAR upload) lives under Settings > Purchase Orders; here it is one status line.
function PurchaseOrders() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canSettings = canOpenSettings(permissions);

  // ── Session (read-only here) ──
  const [session, setSession] = useState(null);        // null = checking, {ok, config_age, cookies_count} or {ok:false}
  const sessionOk = !!(session && session.ok);

  // ── Selection / print ──
  const [location, setLocation] = useState('lancaster');
  const [selectedPos, setSelectedPos] = useState([]);
  const [selectedPosIdMap, setSelectedPosIdMap] = useState({});
  const [printReadyPos, setPrintReadyPos] = useState(null);
  const [printFetching, setPrintFetching] = useState(false);
  const printPopupRef  = useRef(null);
  const printSourceRef = useRef(null);

  // ── PO list / pagination ──
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [cursors, setCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [endCursor, setEndCursor] = useState(null);

  // ── Filters ──
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [{ month, year }, setDateNav] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [pendingDates, setPendingDates] = useState({ start: null, end: null });
  const datePopRef = useRef(null);

  // Sticky toolbar: the table header sticks just under it.
  const tbarRef = useRef(null);
  const [tbarHeight, setTbarHeight] = useState(0);
  useEffect(() => {
    if (!tbarRef.current) return undefined;
    const ro = new ResizeObserver(() => setTbarHeight(tbarRef.current?.offsetHeight || 0));
    ro.observe(tbarRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Details dialog ──
  const [showDetails, setShowDetails] = useState(false);
  const [poDetails, setPoDetails] = useState(null);
  const [detailName, setDetailName] = useState('');
  const detailsRef = useRef(null);
  useEffect(() => {
    const d = detailsRef.current;
    if (!d) return;
    if (showDetails && !d.open) d.showModal();
    if (!showDetails && d.open) d.close();
  }, [showDetails]);

  // ── Notice (replaces the Polaris toast) ──
  const [notice, setNotice] = useState(null);           // {text, tone: 'ok' | 'crit'}
  const showNotice = (text, isError = false) => setNotice({ text, tone: isError ? 'crit' : 'ok' });
  useEffect(() => {
    if (!notice || notice.tone !== 'ok') return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  // ── Toasty easter egg ──
  const [toastyVisible, setToastyVisible] = useState(false);
  useEffect(() => {
    let timeout;
    const schedule = () => {
      const delay = Math.floor(Math.random() * (200000 - 20000 + 1)) + 20000;
      timeout = setTimeout(() => {
        setToastyVisible(true);
        const audio = new Audio('/toasty.mp3');
        audio.play().catch(() => {});
        setTimeout(() => setToastyVisible(false), 2800);
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timeout);
  }, []);

  // ── Fetch a page of POs ──
  const fetchPage = useCallback(async ({ cursor, search, status, fromDate, toDate }) => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      if (cursor) params.set('after', cursor);
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (fromDate) params.set('date_from', fromDate.toISOString().split('T')[0]);
      if (toDate) params.set('date_to', toDate.toISOString().split('T')[0]);
      const response = await authenticatedFetch(`${API_BASE_URL}/api/purchase-orders/list?${params}`);
      if (!response.ok) return;
      const data = await response.json();
      setPurchaseOrders(data.purchase_orders || []);
      setHasNextPage(data.hasNextPage || false);
      setEndCursor(data.endCursor || null);
    } catch (err) {
      if (err.message === 'NO_TOKEN' || err.message === 'AUTH_FAILED') { navigate('/login'); }
    } finally {
      setLoadingList(false);
    }
  }, [navigate]);

  // ── Session check → initial load ──
  const checkSession = useCallback(async () => {
    setSession(null);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/purchase-orders/status`);
      if (response.ok) {
        const data = await response.json();
        const ok = data.status === 'ok';
        setSession({ ok, config_age: data.config_age || '', cookies_count: data.cookies_count || 0 });
        if (ok) fetchPage({ cursor: null, search: '', status: '', fromDate: null, toDate: null });
      } else {
        setSession({ ok: false });
      }
    } catch (err) {
      if (err.message === 'NO_TOKEN' || err.message === 'AUTH_FAILED') { navigate('/login'); return; }
      setSession({ ok: false });
    }
  }, [fetchPage, navigate]);

  useEffect(() => { checkSession(); }, [checkSession]);

  // ── Filter apply / clear ──
  const resetPaging = () => { setCursors([null]); setPageIndex(0); setEndCursor(null); };

  const applyFilters = useCallback(async () => {
    const search = searchInput.trim();
    setAppliedSearch(search);
    setAppliedStatus(statusFilter);
    resetPaging();
    await fetchPage({ cursor: null, search, status: statusFilter, fromDate: dateFrom, toDate: dateTo });
  }, [searchInput, statusFilter, dateFrom, dateTo, fetchPage]);

  const clearFilters = useCallback(() => {
    setSearchInput(''); setAppliedSearch('');
    setStatusFilter(''); setAppliedStatus('');
    setDateFrom(null); setDateTo(null);
    setPendingDates({ start: null, end: null });
    resetPaging();
    fetchPage({ cursor: null, search: '', status: '', fromDate: null, toDate: null });
  }, [fetchPage]);

  // ── Pagination ──
  const handleNextPage = useCallback(async () => {
    const newIndex = pageIndex + 1;
    let cursorForPage = cursors[newIndex];
    if (!cursorForPage) {
      setCursors((prev) => [...prev, endCursor]);
      cursorForPage = endCursor;
    }
    setPageIndex(newIndex);
    await fetchPage({ cursor: cursorForPage, search: appliedSearch, status: appliedStatus, fromDate: dateFrom, toDate: dateTo });
  }, [pageIndex, cursors, endCursor, appliedSearch, appliedStatus, dateFrom, dateTo, fetchPage]);

  const handlePrevPage = useCallback(async () => {
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    await fetchPage({ cursor: cursors[newIndex], search: appliedSearch, status: appliedStatus, fromDate: dateFrom, toDate: dateTo });
  }, [pageIndex, cursors, appliedSearch, appliedStatus, dateFrom, dateTo, fetchPage]);

  // ── Date range ──
  const dateRangeLabel = (() => {
    if (!dateFrom && !dateTo) return 'All dates';
    if (dateFrom && dateTo) return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
    if (dateFrom) return `From ${formatDate(dateFrom)}`;
    return `Until ${formatDate(dateTo)}`;
  })();

  const runWithDates = (from, to) => {
    setDateFrom(from); setDateTo(to);
    setDatePickerVisible(false);
    const search = searchInput.trim();
    setAppliedSearch(search); setAppliedStatus(statusFilter);
    resetPaging();
    fetchPage({ cursor: null, search, status: statusFilter, fromDate: from, toDate: to });
  };
  const applyDateRange = () => runWithDates(pendingDates.start || null, pendingDates.end || null);
  const clearDateRange = () => { setPendingDates({ start: null, end: null }); runWithDates(null, null); };
  const PRESETS = [['All dates', null], ['Last 7 days', 7], ['Last 30 days', 30], ['Last 90 days', 90]];
  const pickPreset = (days) => {
    const to = days ? new Date() : null;
    const from = days ? new Date(Date.now() - days * 86400000) : null;
    setPendingDates({ start: from, end: to });
    runWithDates(from, to);
  };
  useEffect(() => {
    if (!datePickerVisible) return undefined;
    const onDown = (e) => { if (datePopRef.current && !datePopRef.current.contains(e.target)) setDatePickerVisible(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [datePickerVisible]);

  // ── Print — write to popup when data is ready ──
  useEffect(() => {
    if (!printReadyPos || !printSourceRef.current || !printPopupRef.current) return;
    const popup = printPopupRef.current;
    if (popup.closed) { setPrintReadyPos(null); return; }

    const content = printSourceRef.current.innerHTML;
    popup.document.open();
    popup.document.write(`<!DOCTYPE html><html>
<head><meta charset="utf-8"><title>Soccer Post Purchase Orders</title>
<style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    margin: 0 auto;
    background: white;
    color: #202223;
    max-width: 760px;
    padding: 24px 32px;
  }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  table { border-collapse: collapse; width: 100%; }
  thead { display: table-header-group; }
  img { max-width: 100%; }
  @media print { @page { margin: 0.6in; } }
  .po-break { page-break-before: always; break-before: page; }
</style>
</head>
<body>
${content}
<script>
  setTimeout(function() { window.focus(); window.print(); }, 400);
<\/script>
</body></html>`);
    popup.document.close();
    setPrintReadyPos(null);
    printPopupRef.current = null;
  }, [printReadyPos]);

  // ── Print — fetch details then render ──
  const executePrint = async (names, idMap = {}) => {
    if (!names || names.length === 0) {
      showNotice('Tick at least one purchase order to print.', true);
      return;
    }
    const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
    if (!popup) {
      showNotice('Allow pop-ups for this site to print pick sheets.', true);
      return;
    }
    popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>
<body style="font-family:sans-serif;padding:40px;color:#374151">
  <p style="font-size:15px">Loading purchase order details… (fetching bin locations)</p>
</body></html>`);
    printPopupRef.current = popup;
    setPrintFetching(true);

    try {
      const details = await Promise.all(
        names.map(async (name) => {
          try {
            const clean = name.replace(/^#/, '');
            const poId = idMap[name] || idMap[clean];
            const params = new URLSearchParams({ location });
            if (poId) params.set('po_id', poId);
            const res = await authenticatedFetch(
              `${API_BASE_URL}/api/purchase-orders/details/${encodeURIComponent(clean)}?${params}`
            );
            if (res && res.ok) {
              const data = await res.json();
              if (data.success && data.purchase_order) return data.purchase_order;
            }
          } catch {}
          return null;
        })
      );
      setPrintFetching(false);
      const valid = details.filter(Boolean);
      if (valid.length === 0) {
        popup.close();
        printPopupRef.current = null;
        showNotice('Could not load the purchase order details for printing.', true);
        return;
      }
      setPrintReadyPos(valid);
    } catch (err) {
      setPrintFetching(false);
      if (!popup.closed) popup.close();
      printPopupRef.current = null;
      showNotice(`Print failed: ${err.message}`, true);
    }
  };

  const viewPoDetails = async (poName, poId) => {
    setDetailName(poName); setShowDetails(true); setPoDetails(null);
    try {
      const clean = poName.replace(/^#/, '');
      const params = poId ? `?po_id=${encodeURIComponent(poId)}` : '';
      const response = await authenticatedFetch(`${API_BASE_URL}/api/purchase-orders/details/${encodeURIComponent(clean)}${params}`);
      if (response.ok) {
        const data = await response.json();
        setPoDetails(data.purchase_order);
      } else {
        showNotice('Could not load the purchase order details.', true); setShowDetails(false);
      }
    } catch (err) {
      if (err.message === 'NO_TOKEN' || err.message === 'AUTH_FAILED') { navigate('/login'); return; }
      showNotice('Could not load the purchase order details.', true); setShowDetails(false);
    }
  };

  const togglePoSelection = (poName, poId) => {
    const clean = poName.replace(/^#/, '');
    setSelectedPos((prev) => (prev.includes(clean) ? prev.filter((p) => p !== clean) : [...prev, clean]));
    if (poId) {
      setSelectedPosIdMap((prev) => {
        if (prev[clean]) { const next = { ...prev }; delete next[clean]; return next; }
        return { ...prev, [clean]: poId };
      });
    }
  };
  const clearSelection = () => { setSelectedPos([]); setSelectedPosIdMap({}); };
  const isSelected = (po) => selectedPos.includes(po.name.replace(/^#/, ''));
  const allOnPageSelected = purchaseOrders.length > 0 && purchaseOrders.every(isSelected);
  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      const names = new Set(purchaseOrders.map((po) => po.name.replace(/^#/, '')));
      setSelectedPos((prev) => prev.filter((n) => !names.has(n)));
      setSelectedPosIdMap((prev) => { const next = { ...prev }; names.forEach((n) => delete next[n]); return next; });
    } else {
      purchaseOrders.filter((po) => !isSelected(po)).forEach((po) => togglePoSelection(po.name, po.id));
    }
  };

  const hasActiveFilters = !!(appliedSearch || appliedStatus || dateFrom || dateTo);
  const currentPage = pageIndex + 1;
  const count = (st) => purchaseOrders.filter((po) => (po.status || '').toUpperCase() === st).length;
  const detailSelected = detailName ? selectedPos.includes(detailName.replace(/^#/, '')) : false;
  const detailEdges = poDetails?.lineItems?.edges || [];
  const detailQty = detailEdges.reduce((sum, { node: item }) => sum + (item.totalQuantity || item.acceptedQuantity || 0), 0);

  return (
    <>
      {/* Hidden render div for print */}
      {printReadyPos && (
        <div
          ref={printSourceRef}
          style={{ position: 'fixed', left: -10000, top: 0, width: 760, visibility: 'hidden', pointerEvents: 'none', overflow: 'visible' }}
          aria-hidden="true"
        >
          {printReadyPos.map((po, idx) => (
            <React.Fragment key={po.name || idx}>
              {idx > 0 && <div className="po-break" />}
              <POPrintCard po={po} location={location} />
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="uh uh-page">
        <div className="uh-ph">
          <div>
            <h1 className="uh-h1">Shopify Purchase Orders</h1>
            <div className="uh-sub">Find purchase orders, tick the ones to pick, and print their pick sheets for a location.</div>
          </div>
          <div className="uh-statcard" aria-label="This page of results">
            <div className="stats">
              <div className="uh-stat"><span className="v">{sessionOk ? purchaseOrders.length : '—'}</span><span className="k">On this page</span></div>
              <div className="uh-stat"><span className="v">{sessionOk ? count('ORDERED') : '—'}</span><span className="k">Ordered</span></div>
              <div className="uh-stat"><span className="v">{sessionOk ? count('DRAFT') : '—'}</span><span className="k">Draft</span></div>
              <div className="uh-stat"><span className="v">{selectedPos.length}</span><span className="k">Selected</span></div>
            </div>
          </div>
        </div>

        {session && !session.ok && (
          <div className="uh-alert">
            <Ic source={AlertTriangleIcon} sm />
            <div><b>The Shopify admin session has expired.</b> Purchase orders can't be loaded or printed until a fresh HAR file is uploaded.{!canSettings && ' Ask an admin to upload one.'}</div>
            {canSettings && <Link className="uh-btn" to="/settings/purchase-orders" style={{ marginLeft: 'auto' }}>Update session in Settings</Link>}
          </div>
        )}

        {notice && (
          <div className={`uh-alert ${notice.tone}`} role="status">
            <Ic source={notice.tone === 'ok' ? CheckCircleIcon : AlertTriangleIcon} sm /><div>{notice.text}</div>
            <button type="button" className="uh-ibtn" aria-label="Dismiss" style={{ marginLeft: 'auto' }} onClick={() => setNotice(null)}><Ic source={XSmallIcon} sm /></button>
          </div>
        )}

        <div className="uh-tr-grid" style={{ '--tr-tbar-h': `${tbarHeight}px` }}>
          <div className="uh-card">
            <div className="uh-tr-tbar" ref={tbarRef}>
              <div className="uh-daterange" ref={datePopRef}>
                <button type="button" className="uh-btn" aria-expanded={datePickerVisible} disabled={!sessionOk} onClick={() => setDatePickerVisible((v) => !v)}>
                  <Ic source={CalendarIcon} sm />{dateRangeLabel}<Ic source={ChevronDownIcon} sm />
                </button>
                {datePickerVisible && (
                  <div className="uh-daterange-pop">
                    <div className="presets">
                      {PRESETS.map(([label, days]) => <button key={label} type="button" onClick={() => pickPreset(days)}>{label}</button>)}
                    </div>
                    <div className="cal">
                      <DatePicker
                        month={month}
                        year={year}
                        onChange={(range) => setPendingDates(range)}
                        onMonthChange={(m, y) => setDateNav({ month: m, year: y })}
                        selected={pendingDates.start ? pendingDates : undefined}
                        allowRange
                        disableDatesAfter={new Date()}
                      />
                      <div className="acts">
                        <button type="button" className="uh-btn slim" onClick={clearDateRange}>Clear</button>
                        <button type="button" className="uh-btn primary slim" onClick={applyDateRange}>Apply</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <select className="uh-select" aria-label="Status" value={statusFilter} disabled={!sessionOk} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_FILTERS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              <div className="uh-search">
                <Ic source={SearchIcon} sm />
                <input
                  value={searchInput}
                  disabled={!sessionOk}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  placeholder="PO #, e.g. PO7968"
                  aria-label="PO number"
                />
              </div>
              <button type="button" className="uh-btn primary" onClick={applyFilters} disabled={!sessionOk || loadingList}><Ic source={SearchIcon} sm />Search</button>
              {hasActiveFilters && <button type="button" className="uh-btn" onClick={clearFilters}>Clear</button>}
              <span className="uh-sp" />
              {sessionOk && (
                <>
                  <span className="uh-help">{hasActiveFilters ? 'Filtered' : `Page ${currentPage}`} · {purchaseOrders.length} result{purchaseOrders.length !== 1 ? 's' : ''}</span>
                  <button
                    type="button" className="uh-link" disabled={loadingList}
                    onClick={() => fetchPage({ cursor: cursors[pageIndex], search: appliedSearch, status: appliedStatus, fromDate: dateFrom, toDate: dateTo })}
                  >
                    <Ic source={RefreshIcon} sm />Refresh
                  </button>
                </>
              )}
            </div>

            <div className="uh-tscroll uh-tr-scroll">
              <table className="uh-tbl uh-tr-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input type="checkbox" className="uh-tr-check" checked={allOnPageSelected} disabled={!purchaseOrders.length} onChange={toggleAllOnPage} aria-label="Select all on this page" />
                    </th>
                    <th>PO #</th><th>Date</th><th>Supplier</th><th>Destination</th><th>Status</th><th className="r">Units</th><th />
                  </tr>
                </thead>
                <tbody>
                  {session === null && <tr><td colSpan={8} className="uh-empty">Checking the Shopify session…</td></tr>}
                  {session && !session.ok && <tr><td colSpan={8} className="uh-empty">Session expired. Upload a HAR file under Settings › Purchase Orders to load purchase orders.</td></tr>}
                  {sessionOk && loadingList && purchaseOrders.length === 0 && <tr><td colSpan={8} className="uh-empty">Loading purchase orders…</td></tr>}
                  {sessionOk && !loadingList && purchaseOrders.length === 0 && <tr><td colSpan={8} className="uh-empty">No purchase orders found.</td></tr>}
                  {sessionOk && purchaseOrders.map((po) => {
                    const sel = isSelected(po);
                    return (
                      <tr key={po.id || po.name} className={sel ? 'uh-tr-sel' : ''}>
                        <td><input type="checkbox" className="uh-tr-check" checked={sel} onChange={() => togglePoSelection(po.name, po.id)} aria-label={`Select ${po.name}`} /></td>
                        <td className="nm">{po.name}</td>
                        <td className="uh-muted uh-num uh-tr-date">{(po.ordered_at || '').slice(0, 10) || '—'}</td>
                        <td>{po.supplier || '—'}</td>
                        <td>{po.destination || '—'}</td>
                        <td><StatusPill tone={statusTone(po.status)}>{statusText(po.status)}</StatusPill></td>
                        <td className="r uh-num">{po.total_quantity ?? '—'}</td>
                        <td className="r"><button type="button" className="uh-btn slim" onClick={() => viewPoDetails(po.name, po.id)}>Details</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasActiveFilters && hasNextPage && purchaseOrders.length < 50 && (
              <div className="uh-tfoot"><span>Looked through the most recent purchase orders. Use Next to keep looking.</span></div>
            )}
            {sessionOk && (
              <div className="uh-tr-pager">
                <button type="button" className="uh-btn slim" disabled={pageIndex === 0 || loadingList} onClick={handlePrevPage}>← Previous</button>
                <span>Page {currentPage}</span>
                <button type="button" className="uh-btn slim" disabled={!hasNextPage || loadingList} onClick={handleNextPage}>Next →</button>
              </div>
            )}
          </div>

          <div className="uh-col uh-tr-side">
            <div className="uh-card">
              <div className="uh-card-h"><h2 className="uh-h2 lg">Print pick sheets</h2></div>
              <div className="uh-card-b uh-tr-print">
                <div className="uh-fld">
                  <label htmlFor="po-loc">Location</label>
                  <select id="po-loc" className="uh-inp" value={location} onChange={(e) => setLocation(e.target.value)}>
                    {LOCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="uh-help">Printed sheets show the bins for this location.</span>
                </div>
                <div>
                  <div className="uh-tr-selhead">
                    <h3 className="uh-h2">Selected</h3><span className="uh-tcount">{selectedPos.length}</span><span className="uh-sp" />
                    {selectedPos.length > 0 && <button type="button" className="uh-link" onClick={clearSelection}>Clear selection</button>}
                  </div>
                  {selectedPos.length > 0 ? (
                    <div className="uh-tr-chips">
                      {selectedPos.map((name) => (
                        <span key={name} className="chip">{name}
                          <button type="button" aria-label={`Remove ${name}`} onClick={() => togglePoSelection(name)}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : <span className="uh-help">Nothing selected. Tick purchase orders in the list.</span>}
                </div>
                <button
                  type="button" className={`uh-btn primary wide${printFetching ? ' busy' : ''}`}
                  disabled={!sessionOk || selectedPos.length === 0 || printFetching}
                  onClick={() => executePrint(selectedPos, selectedPosIdMap)}
                >
                  <Ic source={PrintIcon} sm />
                  {printFetching ? 'Preparing…' : selectedPos.length ? `Print ${selectedPos.length} pick sheet${selectedPos.length === 1 ? '' : 's'}` : 'Print'}
                </button>
              </div>
            </div>

            <div className="uh-card uh-po-sess">
              {session === null
                ? <StatusPill tone="neu">Checking…</StatusPill>
                : <StatusPill tone={session.ok ? 'ful' : 'can'}>{session.ok ? 'Session active' : 'Session expired'}</StatusPill>}
              <div className="txt">
                <b>Shopify admin session</b>
                <span>{session === null ? 'Checking the saved session' : session.ok ? `Updated ${sessionAgeText(session)} · ${session.cookies_count} cookies` : canSettings ? 'Upload a fresh HAR file under Settings › Purchase Orders' : 'Ask an admin to upload a fresh HAR file'}</span>
              </div>
            </div>
          </div>
        </div>

        <dialog className="uh-dlg uh-tr-dlg" ref={detailsRef} aria-labelledby="po-dlg-h" onCancel={() => setShowDetails(false)}>
          <div className="uh-dlg-h">
            <h2 id="po-dlg-h">{detailName || 'Purchase order'}</h2>
            {poDetails && <StatusPill tone={statusTone(poDetails.receiptStatus || poDetails.status)}>{statusText(poDetails.receiptStatus || poDetails.status)}</StatusPill>}
            <button type="button" className="uh-ibtn" aria-label="Close" onClick={() => setShowDetails(false)}><Ic source={XSmallIcon} /></button>
          </div>
          <div className="uh-dlg-b">
            {!poDetails ? <span className="uh-muted">Loading purchase order details…</span> : (
              <>
                <div className="uh-tr-meta">
                  <div><div className="k">Supplier</div><b>{(poDetails.supplier || poDetails.supplierTarget || {}).name || 'N/A'}</b></div>
                  <div><div className="k">Destination</div><b>{(poDetails.destination || poDetails.destinationTarget || {}).name || 'N/A'}</b></div>
                  <div><div className="k">Ordered</div>{(poDetails.orderedAt || '').slice(0, 10) || 'N/A'}</div>
                  <div><div className="k">Total units</div><b>{detailQty || 'N/A'}</b></div>
                </div>
                {detailEdges.length === 0 ? <span className="uh-muted">No line items found.</span> : (
                  <div>
                    <h3 className="uh-h2" style={{ marginBottom: 4 }}>Line items ({detailEdges.length})</h3>
                    {detailEdges.map(({ node: item }, idx) => {
                      const sku = item.inventoryItem?.variant?.sku || item.supplierSku || '—';
                      const qty = item.totalQuantity || item.acceptedQuantity || 0;
                      const imageUrl = item.image_url || item.image?.transformedSrc;
                      const variant = item.description && item.description !== 'Default Title' ? item.description : '';
                      return (
                        <div key={item.id || idx} className="uh-tr-li">
                          <ProductImage imageUrl={imageUrl} productTitle={item.title} />
                          <div style={{ minWidth: 0 }}>
                            <div className="nm">{item.title || '—'}</div>
                            <div className="uh-help uh-mono">SKU {sku}{variant ? ` · ${variant}` : ''}</div>
                          </div>
                          <b className="uh-num">Qty {qty}</b>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="uh-dlg-f">
            <button type="button" className="uh-btn" onClick={() => setShowDetails(false)}>Close</button>
            {detailName && (
              <button type="button" className="uh-btn primary" onClick={() => { togglePoSelection(detailName, poDetails?.id); setShowDetails(false); }}>
                {detailSelected ? 'Remove from printing' : 'Select for printing'}
              </button>
            )}
          </div>
        </dialog>
      </div>

      {/* Toasty! */}
      <style>{`
        @keyframes toasty {
          0%   { transform: translateY(115%) rotate(-30deg); }
          15%  { transform: translateY(0%) rotate(-30deg); }
          75%  { transform: translateY(0%) rotate(-30deg); }
          100% { transform: translateY(115%) rotate(-30deg); }
        }
      `}</style>
      {toastyVisible && (
        <div style={{
          position: 'fixed', bottom: 0, right: '0px',
          width: '480px', height: '215px',
          overflow: 'hidden', zIndex: 9999, pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', bottom: 0, right: 0, width: '480px',
            animation: 'toasty 2.8s ease-in-out forwards',
            transformOrigin: 'bottom right',
          }}>
            <img src="/karsh.png" alt="Toasty!" style={{ width: '100%', display: 'block' }} />
          </div>
        </div>
      )}
    </>
  );
}

export default PurchaseOrders;
